import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Button, Box, Divider, Grid, Typography } from "@material-ui/core";
import Icon from "@material-ui/core/Icon";
import {
  AttachFile as AttachFileIcon,
  LocationOn as LocationIcon,
  Event as EventIcon
} from "@material-ui/icons";
import styled from "styled-components";
import DOMPurify from "dompurify";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { communicationType } from "../../../types";
import Dialog from "../../../waybee-ui/Dialog";
import RoundedAvatar from "../../../waybee-ui/RoundedAvatar";
import WuiButton from "../../../waybee-ui/Button";
import CommunicationService from "../../../services/CommunicationService";
import UserService from "../../../services/UserService";
import isClassDiaryDemo from "../../../demo/isClassDiaryDemo";

const HeaderTitle = styled.p`
  margin: 0;
  font-size: 24px;
  color: #00303b;
  font-weight: 500;
`;

const HeaderDate = styled.p`
  margin: 0;
  font-size: 18px;
  color: rgba(0, 0, 0, 0.5);
`;

const CommunicationDialogHeader = ({ title, date, photo, t }) => {
  return (
    <Box p={3} display="flex" alignItems="stretch">
      <RoundedAvatar
        size={55}
        imageSrc={photo}
        imageAlt={t("communication.dialog.logoAlt")}
        icon="user"
      />
      <Box
        pl={2.5}
        display="inline-flex"
        flexDirection="column"
        justifyContent="space-between"
      >
        <HeaderTitle>{title}</HeaderTitle>
        <HeaderDate>{moment(date).format("DD/MM/YYYY HH:mm")}</HeaderDate>
      </Box>
    </Box>
  );
};

CommunicationDialogHeader.propTypes = {
  title: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  photo: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired
};

const AttachmentButton = styled(Button)`
  padding: 30px;
  text-transform: none;
`;

const AttachName = styled.span`
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CommunicationDialogAttachments = ({ attachments }) => {
  return (
    <>
      <Divider />
      <Grid container>
        {attachments.map(attachment => (
          <Grid key={attachment.id} item sm={3}>
            <AttachmentButton
              fullWidth
              color="primary"
              href={attachment.path}
              target="_blank"
            >
              <AttachFileIcon />
              <AttachName>{attachment.fileName}</AttachName>
            </AttachmentButton>
          </Grid>
        ))}
      </Grid>
    </>
  );
};

CommunicationDialogAttachments.propTypes = {
  attachments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      path: PropTypes.string,
      fileName: PropTypes.string
    })
  ).isRequired
};

const EventInfoText = styled(Typography)`
  font-size: 14px;
  display: flex;
  align-items: center;
  margin: 10px 0;
  p {
    margin: 0 10px;
  }
`;

const EventDate = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  height: 100%;
  border-right: 1px solid #ccc;
`;

const EventDay = styled(Typography)`
  font-size: 20px;
  font-weight: bold;
`;

const EventMonth = styled(Typography)`
  font-size: 18px;
`;

const EventInfo = ({ date, location }) => {
  return (
    <>
      <Divider />
      <Grid container spacing={2}>
        <Grid item xs={2}>
          <EventDate>
            <EventDay color="primary">{moment(date).format("DD")}</EventDay>
            <EventMonth color="primary">
              {moment(date)
                .format("MMM")
                .toUpperCase()}
            </EventMonth>
          </EventDate>
        </Grid>
        <Grid item xs={10}>
          <EventInfoText color="primary">
            <LocationIcon />
            <p>{location}</p>
          </EventInfoText>
          <EventInfoText color="primary">
            <EventIcon />
            <p>{moment(date).format("HH:mm")}</p>
          </EventInfoText>
        </Grid>
      </Grid>
    </>
  );
};

EventInfo.propTypes = {
  date: PropTypes.string.isRequired,
  location: PropTypes.string.isRequired
};

const EventButtonText = styled.p`
  margin-left: 10px;
`;

const EventButton = styled(Button)`
  &.default {
    color: rgba(0, 0, 0, 0.5);
  }
`;

const EventActions = ({ onClick, interest, presence, t }) => {
  return (
    <>
      <Divider />
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <EventButton
            fullWidth
            onClick={() => onClick("interest")}
            color={interest ? "primary" : undefined}
            className={interest ? "HAHAHAHAHAHA" : "default"}
          >
            <Icon>{interest ? "star" : "star_outline"}</Icon>
            <EventButtonText>
              {t("communication.dialog.interest")}
            </EventButtonText>
          </EventButton>
        </Grid>
        <Grid item xs={6}>
          <EventButton
            fullWidth
            onClick={() => onClick("presence")}
            color={presence ? "primary" : undefined}
            className={presence ? "HAHAHAHAHAHA" : "default"}
          >
            <Icon>{presence ? "check_circle" : "check_circle_outline"}</Icon>
            <EventButtonText>
              {t("communication.dialog.confirmPresence")}
            </EventButtonText>
          </EventButton>
        </Grid>
      </Grid>
    </>
  );
};

EventActions.propTypes = {
  onClick: PropTypes.func.isRequired,
  interest: PropTypes.bool.isRequired,
  presence: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired
};

/** Frame centralizado, mais estreito que o modal — evita imagem “esticada” em telas largas. */
const CommunicationCoverFrame = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 0 24px 8px;
  box-sizing: border-box;
`;

const CommunicationCover = styled.img`
  display: block;
  width: 100%;
  max-width: 520px;
  max-height: 280px;
  height: auto;
  object-fit: contain;
  border-radius: 4px;
`;

const CommunicationDialog = ({
  open,
  onClose,
  communication,
  updateCommunication
}) => {
  const { t } = useTranslation();
  const [ackSubmitting, setAckSubmitting] = useState(false);

  const needsMandatoryAck =
    communication.interaction && communication.interaction.hasAck === false;

  const handleDialogClose = useCallback(
    (event, reason) => {
      if (needsMandatoryAck) {
        // eslint-disable-next-line no-alert
        window.alert(t("communication.dialog.mustAck"));
        return;
      }
      onClose(event, reason);
    },
    [needsMandatoryAck, onClose, t]
  );

  const onConfirmAck = async () => {
    if (!needsMandatoryAck || !communication.userId) return;
    setAckSubmitting(true);
    try {
      await UserService.updateAckCommunication(
        true,
        communication.id,
        communication.userId
      );
      updateCommunication({
        ...communication,
        interaction: {
          ...communication.interaction,
          hasAck: true
        }
      });
      if (isClassDiaryDemo()) {
        window.location.reload();
      } else {
        onClose();
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      // eslint-disable-next-line no-alert
      window.alert(t("communication.dialog.ackError"));
    } finally {
      setAckSubmitting(false);
    }
  };

  const onClickLinkButton = async () => {
    if (communication.interaction && !communication.interaction.clickedAt) {
      await CommunicationService.updateInteractions(
        { hasClicked: true },
        communication.id
      );
    }
  };

  const onClickEventAction = async type => {
    if (!communication.interaction) return;
    let data = {};
    if (type === "interest") {
      const hadInterest = !communication.interaction.hadInterestAt;
      data = { hadInterest };
      // eslint-disable-next-line no-param-reassign
      communication.interaction.hadInterestAt = hadInterest
        ? new Date().toISOString
        : "";
    }
    if (type === "presence") {
      const confirmedPresence = !communication.interaction.confirmedPresenceAt;
      data = { confirmedPresence };
      // eslint-disable-next-line no-param-reassign
      communication.interaction.confirmedPresenceAt = confirmedPresence
        ? new Date().toISOString
        : "";
    }
    await CommunicationService.updateInteractions(data, communication.id);
    updateCommunication(communication);
  };

  return (
    <Dialog
      scroll="body"
      open={open}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      closeButton
      loading={ackSubmitting}
    >
      <CommunicationDialogHeader
        date={communication.date}
        photo={communication.photo}
        title={communication.title}
        t={t}
      />

      {communication.cover && (
        <CommunicationCoverFrame>
          <CommunicationCover
            src={communication.cover}
            alt={communication.title || ""}
          />
        </CommunicationCoverFrame>
      )}

      <Box p={3}>
        <div
          // eslint-disable-next-line
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(communication.bodyMessage)
          }}
        />
      </Box>
      {communication.button.link && communication.button.text && (
        <Box m={1} textAlign="center">
          <WuiButton
            onClick={onClickLinkButton}
            href={communication.button.link}
            color="primary"
            target="_blank"
            variant="contained"
          >
            {communication.button.text}
          </WuiButton>
        </Box>
      )}
      {communication.event.date && (
        <EventInfo
          date={communication.event.date}
          location={communication.event.location}
        />
      )}
      {communication.attachments && communication.attachments.length > 0 && (
        <CommunicationDialogAttachments
          attachments={communication.attachments}
        />
      )}

      {communication.event.date && (
        <EventActions
          onClick={onClickEventAction}
          presence={communication.interaction.confirmedPresenceAt}
          interest={communication.interaction.hadInterestAt}
          t={t}
        />
      )}

      {needsMandatoryAck && (
        <>
          <Divider />
          <Box
            p={3}
            display="flex"
            justifyContent="flex-end"
            flexWrap="wrap"
            style={{ gap: 8 }}
          >
            <WuiButton
              color="primary"
              variant="contained"
              disabled={ackSubmitting}
              onClick={onConfirmAck}
            >
              {t("communication.dialog.ack")}
            </WuiButton>
          </Box>
        </>
      )}
    </Dialog>
  );
};

CommunicationDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  communication: communicationType.isRequired,
  updateCommunication: PropTypes.func.isRequired
};

CommunicationDialog.defaultProps = {
  open: false,
  onClose: () => {}
};

export default CommunicationDialog;
